import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field, CccisdSelect, CccisdCheckboxInput } from 'cccisd-formik';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import Table from 'cccisd-table';
import Spinner from 'cccisd-spinner';
import notification from 'cccisd-notification';
import Loader from 'cccisd-loader';
import Cross from 'cccisd-icons/cross3';
import style from './style.css';
import appQuery from './appQuery.graphql';
import Accordion from '../../accordion.js';

const Boilerplate = window.cccisd.boilerplate;

export default class ProductionDeployForm extends React.Component {
    static propTypes = {
        closeModal: PropTypes.func,
    };

    modal = React.createRef();

    state = {
        apphandle: null,
        tags: [],
        parameters: [],
        parametersCollapsed: true,
        loading: true,
        spinner: false,
        apps: [],
    };

    componentDidMount = async () => {
        const response = await client.query({
            query: appQuery,
            fetchPolicy: 'network-only',
        });

        this.setAppData(response.data.groups.organizationList);

        this.setState({
            loading: false,
        });
    };

    // Prevent loss of unsaved changes
    confirmClose = () => {
        this.props.closeModal();
    };

    handleCollapse = () => {
        this.setState({
            parametersCollapsed: !this.state.parametersCollapsed,
        });
    };

    appSelect = async row => {
        this.setState({
            spinner: true,
        });
        const response = await axios
            .get(
                Boilerplate.route('api.monitoring.devops.app-info') + '?apphandle=' + row.handle + '&context=production'
            )
            .catch(() => {
                this.setState({ spinner: false });
            });

        this.setState({
            apphandle: row.handle,
            tags: response.data.data.tags,
            parameters: response.data.data.parameters,
            parametersCollapsed: true,
            spinner: false,
        });
    };

    unselectApp = () => {
        this.setState({
            apphandle: null,
        });
    };

    setAppData = orgs => {
        let apps = [];

        orgs.forEach(org =>
            apps.push({
                id: org.group.groupId,
                label: org.group.label,
                handle: org.fields.git_handle,
                settings: org.group.settings,
            })
        );

        this.setState({
            apps,
        });
    };

    getAppInfo = () => {
        let info = {};

        this.state.apps.forEach(app => {
            if (app.handle === this.state.apphandle) {
                info = app;
            }
        });

        return info;
    };

    getTagList = () => {
        let taglist = [{ value: null, label: 'Choose build tag...' }];
        this.state.tags.forEach(tag => {
            let label = tag;
            if (taglist.length === 1) {
                label = tag + ' (latest)';
            }
            taglist.push({
                value: tag,
                label,
            });
        });

        return taglist;
    };

    onSubmit = async (values, actions) => {
        this.setState({
            loading: true,
        });

        let formattedData = values;
        formattedData.apphandle = this.state.apphandle;

        if (values.method === 'FARGATE') {
            formattedData.webserver = null;
        }
        const response = await axios
            .post(Boilerplate.route('api.monitoring.devops.deploy.production'), { ...formattedData })
            .catch(() => {
                this.setState({ loading: false });
            });

        this.setState({
            loading: false,
        });

        if (!response || response.data.status !== 'success') {
            notification({ message: 'An error occured', type: 'danger' });
        }

        this.props.closeModal();
    };

    validate = values => {
        const errors = {};

        if (!this.state.apphandle) {
            notification({
                type: 'warning',
                message: 'Application is required.',
            });
            errors.tag = 'Application is required.';
            return errors;
        }
        if (!values.tag || values.tag === 'Choose build tag...') {
            errors.tag = 'Build tag is required';
        }

        return errors;
    };

    appColumns = [
        { name: 'handle', label: 'Handle', filter: true, sort: true },
        { name: 'label', label: 'Name', filter: true, sort: true },
    ];

    setRowClass = row => {
        if (row.handle === this.state.apphandle) {
            return style.highlighted;
        }

        return style.gray;
    };

    renderParametersTable = () => {
        const parameterList = Object.entries(this.state.parameters);

        return (
            <table>
                <tbody>
                    {parameterList.map(value => (
                        <tr key={value[0]}>
                            <td>{value[0]}</td>
                            <td>{value[1]}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    render() {
        const { loading, apphandle, spinner, parameters } = this.state;

        const methods = [
            { value: null, label: 'AUTOMATIC' },
            { value: 'SHARED', label: 'SHARED' },
            { value: 'FARGATE', label: 'FARGATE' },
        ];

        const webservers = [
            { value: null, label: 'AUTOMATIC' },
            { value: 'shared-prod1', label: 'shared-prod1' },
            { value: 'shared-prod2', label: 'shared-prod2' },
            { value: 'shared-prod3', label: 'shared-prod3' },
        ];

        const queueservers = [
            { value: null, label: 'AUTOMATIC' },
            { value: 'queue-prod2', label: 'queue-prod2' },
            { value: 'queue-prod3', label: 'queue-prod3' },
        ];

        let prodLabel = 'No current production build';
        let method = '';
        let webserver = '';
        let queueserver = '';

        const app = this.getAppInfo();

        if (app && app.settings) {
            if (
                app.settings.environments &&
                app.settings.environments.production &&
                app.settings.environments.production.current_version
            ) {
                prodLabel = 'Current production build: ' + app.settings.environments.production.current_version;
            }
            if (parameters) {
                if (parameters.DEPLOY_APP_TARGET) {
                    if (parameters.DEPLOY_APP_TARGET === 'FARGATE') {
                        method = 'FARGATE';
                        webserver = '';
                    } else {
                        method = 'SHARED';
                        webserver = parameters.DEPLOY_APP_TARGET;
                    }
                }
                if (parameters.DEPLOY_QUEUE_TARGET) {
                    queueserver = parameters.DEPLOY_QUEUE_TARGET;
                }
            }
        }
        if (loading) {
            return <Loader loading />;
        }

        return (
            <>
                <div className={style.replaceClose}>
                    <button className="close" type="button" onClick={this.confirmClose}>
                        <Cross />
                    </button>
                </div>
                <Formik
                    key={apphandle}
                    initialValues={{}}
                    validate={this.validate}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={this.onSubmit}
                    render={({ values, errors }) => {
                        return (
                            <Form>
                                {!apphandle && (
                                    <>
                                        {!spinner && (
                                            <div className={style.selectApp}>
                                                <h4>Select Application to Deploy</h4>
                                                <Table
                                                    columns={this.appColumns}
                                                    data={this.state.apps}
                                                    onRowClick={this.appSelect}
                                                    setRowClass={this.setRowClass}
                                                    perPage={10}
                                                    showPerPageOptions={false}
                                                    showRowClickable
                                                />
                                            </div>
                                        )}
                                        {spinner && (
                                            <div style={{ minHeight: '270px', position: 'relative' }}>
                                                <Spinner options={{ width: 4, radius: 15, height: 4 }} />
                                            </div>
                                        )}
                                    </>
                                )}
                                {apphandle && (
                                    <div style={{ minHeight: '270px' }}>
                                        <h4>
                                            {app.label} / {app.handle}{' '}
                                            <span onClick={this.unselectApp} className={style.unselect}>
                                                <Cross />
                                            </span>
                                        </h4>
                                        <div>
                                            <b>Tag:</b>
                                            <Field name="tag" component={CccisdSelect} options={this.getTagList()} />
                                        </div>
                                        <div>
                                            <div style={{ marginBottom: '1em' }}>{prodLabel}</div>
                                            <table className={style.settings_table}>
                                                <tbody>
                                                    <tr>
                                                        <td className={style.settings_key}>Deployment method</td>
                                                        <td className={style.settings_value}>{method}</td>
                                                    </tr>
                                                    {method !== 'FARGATE' && (
                                                        <tr>
                                                            <td className={style.settings_key}>Web server</td>
                                                            <td className={style.settings_value}>{webserver}</td>
                                                        </tr>
                                                    )}
                                                    <tr>
                                                        <td className={style.settings_key}>Queue server</td>
                                                        <td className={style.settings_value}>{queueserver}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            <div>
                                                <Field
                                                    name="override"
                                                    label="Override production settings"
                                                    component={CccisdCheckboxInput}
                                                />
                                                {values.override && (
                                                    <div>
                                                        <Field
                                                            name="method"
                                                            label="Production Deployment method"
                                                            component={CccisdSelect}
                                                            options={methods}
                                                        />
                                                        {(values.method === 'SHARED' ||
                                                            ((!values.method || values.method === 'AUTOMATIC') &&
                                                                method === 'SHARED')) && (
                                                            <Field
                                                                name="webserver"
                                                                label="Production Web server"
                                                                component={CccisdSelect}
                                                                options={webservers}
                                                            />
                                                        )}
                                                        <Field
                                                            name="queueserver"
                                                            label="Production Queue server"
                                                            component={CccisdSelect}
                                                            options={queueservers}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <Accordion
                                                title="Production Parameters"
                                                id="parameters"
                                                handleCollapse={this.handleCollapse}
                                                collapsed={this.state.parametersCollapsed}
                                            >
                                                {this.renderParametersTable()}
                                            </Accordion>
                                        </div>
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginTop: '1em',
                                    }}
                                >
                                    <button className="btn btn-primary" type="button" onClick={this.confirmClose}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        style={{
                                            backgroundColor: '#F0AD4E',
                                            borderColor: '#fff',
                                        }}
                                    >
                                        Deploy to Production
                                    </button>
                                </div>
                            </Form>
                        );
                    }}
                />
            </>
        );
    }
}
